// GAVION ai-email (demo): the twin of supabase/functions/_shared/ai/contextRepo.ts (a ContextDb) over a FakeRest
// RecordStore, so the fake drafter reads the same context shape as the model. Same scoping as the Supabase port: every
// read is filtered on the one company (or on ids read from that company's timeline), and the timeline passes at most
// 61 rows (the 61st only tells the builder that older history was cut).
import type { RecordStore } from "../../dialer/api/recordStore";
import {
  amountHiddenFor,
  holdsDealRole,
  type DealRoleFields,
} from "../../domain/deals/dealRoles";
import {
  loadContextInput,
  type ContextDb,
  type ContextRequest,
  type TextKind,
} from "../../../../../supabase/functions/_shared/ai/contextLoad.ts";
import type {
  ContextInput,
  TimelineRow,
} from "../../../../../supabase/functions/_shared/ai/types.ts";

type Row = Record<string, unknown> & { id: number };
const same = (a: unknown, b: unknown) => String(a) === String(b);
const str = (v: unknown): string | null =>
  typeof v === "string" && v.trim() !== "" ? v.trim() : null;

const TEXT_SOURCE: Record<
  TextKind,
  {
    table: string;
    column: string;
    scope: "company" | "company_or_null" | "none";
  }
> = {
  company_note: { table: "company_notes", column: "text", scope: "company" },
  contact_note: { table: "contact_notes", column: "text", scope: "none" },
  deal_note: { table: "deal_notes", column: "text", scope: "none" },
  call: { table: "calls", column: "note", scope: "company" },
  task: { table: "tasks", column: "text", scope: "company_or_null" },
};

export async function buildFakeContext(
  store: RecordStore,
  timeline: (companyId: number) => Promise<TimelineRow[]>,
  r: ContextRequest,
  /** Demo: the call just logged, when the store has no row for it yet (DialerScreen passes its outcome). */
  loggedCall?: { id: number; outcome: string | null; email: string | null },
): Promise<ContextInput | null> {
  const list = (t: string) => store.list<Row>(t).catch(() => [] as Row[]);
  const db: ContextDb = {
    company: async (id) =>
      (await list("companies")).find((c) => same(c.id, id)) ?? null,
    contacts: async (companyId) =>
      (await list("contacts"))
        .filter((c) => same(c.company_id, companyId))
        .slice(0, 20),
    deal: async (companyId, dealId, hidden, viewerSalesId) => {
      const deals = (await list("deals")).filter(
        (d) =>
          same(d.company_id, companyId) && !hidden.some((h) => same(h, d.id)),
      );
      const row =
        dealId !== null
          ? (deals.find((d) => same(d.id, dealId)) ?? null)
          : (deals
              .filter(
                (d) =>
                  d.stage !== "gagne" && d.stage !== "perdu" && !d.archived_at,
              )
              .at(-1) ?? null);
      // GAVION deal amount visibility (72_): same rule as contextRepo (a demo sales row without administrator: false is
      // an admin)
      if (row && viewerSalesId !== undefined) {
        const me = (await list("sales")).find((s) => same(s.id, viewerSalesId));
        const viewer = {
          salesId: viewerSalesId,
          isAdmin: me?.administrator !== false,
        };
        if (amountHiddenFor(row as DealRoleFields, viewer))
          return { ...row, amount: null };
      }
      return row;
    },
    // GAVION deal visibility: same rule as contextRepo (a demo sales row without administrator: false is not limited)
    hiddenDealIds: async (companyId, salesId) => {
      const me = (await list("sales")).find((s) => same(s.id, salesId));
      if (me?.administrator !== false) return [];
      return (await list("deals"))
        .filter(
          (d) =>
            same(d.company_id, companyId) &&
            !holdsDealRole(d as DealRoleFields, salesId),
        )
        .map((d) => Number(d.id));
    },
    timeline: async (companyId) => (await timeline(companyId)).slice(0, 61),
    texts: async (kind, ids, companyId) => {
      const t = TEXT_SOURCE[kind];
      const wanted = new Set(ids.map(String));
      return (await list(t.table))
        .filter((x) => wanted.has(String(x.id)))
        .filter((x) =>
          t.scope === "company"
            ? same(x.company_id, companyId)
            : t.scope === "company_or_null"
              ? x.company_id == null || same(x.company_id, companyId)
              : true,
        )
        .map((x) => ({ id: Number(x.id), text: str(x[t.column]) }));
    },
    noteSummaries: async (ids, companyId, hidden) => {
      const wanted = new Set(ids.map(String));
      return (await list("meeting_notes"))
        .filter(
          (x) =>
            wanted.has(String(x.id)) &&
            (x.company_id == null || same(x.company_id, companyId)) &&
            (x.deal_id == null || !hidden.some((h) => same(h, x.deal_id))),
        )
        .map((x) => ({ id: Number(x.id), summary: str(x.summary_markdown) }));
    },
    objections: async (trade) =>
      (await list("objections"))
        .filter(
          (o) => o.active !== false && (o.trade == null || o.trade === trade),
        )
        .slice(0, 10)
        .map((o) => ({
          objection: String(o.objection),
          response: String(o.response),
        })),
    aiConfig: async () => {
      const cfg = (
        await store.get<
          Row & { config?: { gavion?: { ai?: Record<string, unknown> } } }
        >("configuration", 1)
      )?.config;
      return cfg?.gavion?.ai ?? {};
    },
    call: async (id, companyId) => {
      const row = (await list("calls")).find(
        (c) => same(c.id, id) && same(c.company_id, companyId),
      );
      if (row) return { outcome: str(row.outcome), email: str(row.email) };
      return loggedCall && same(loggedCall.id, id)
        ? { outcome: loggedCall.outcome, email: loggedCall.email }
        : null;
    },
    // owner ruling D5/P4: slug 'brochure' in the lead's language, not archived; the demo seeds none -> default text
    brochureTemplate: async (language) => {
      const row = (await list("email_templates")).find(
        (t) =>
          t.slug === "brochure" &&
          t.language === language &&
          t.archived !== true,
      );
      return row && str(row.subject) && str(row.body_html)
        ? { subject: String(row.subject), body_html: String(row.body_html) }
        : null;
    },
    rep: async (salesId) =>
      (await list("sales")).find((s) => same(s.id, salesId)) ?? null,
    style: async (salesId) =>
      (await list("ai_style_profiles")).find((s) =>
        same(s.sales_id, salesId),
      ) ?? null,
  };
  return loadContextInput(db, r);
}
